/**
 * ConnectivityService
 *
 * Purpose:
 *   Tracks whether the device currently reports a network connection and
 *   publishes it as an observable the app can react to. ReceivingMgr cannot do
 *   useful work offline — every PO lookup, slip extraction, print request and log
 *   submission is a round trip — so the one worthwhile thing to do with the
 *   signal is say so plainly, which {@link OfflineBannerComponent} does.
 *
 * Responsibility:
 *   Connectivity state, and nothing else. It deliberately does NOT cache, queue
 *   or replay anything: no PO lookup is stashed, no submission is held for later
 *   sync, and nothing of that shape belongs here. A log entry that appeared to
 *   succeed on the floor and then failed to reach the API an hour later would be
 *   worse than one that visibly failed at the time — the receiver is standing in
 *   front of the delivery and can act on a failure they can see.
 *
 * Notes:
 *   - `navigator.onLine` answers "is there a network interface with a route",
 *     not "can I reach the API". A phone on warehouse wifi with a dead uplink
 *     reports online. So a `false` here is trustworthy (there is definitely no
 *     connection) while a `true` is only probably right — which is why the banner
 *     speaks to the false case only, and why the app's normal request-failure
 *     handling still has to stand on its own.
 *   - {@link init} registers its listeners from inside Angular's zone, so zone.js
 *     patches them and a change repaints the banner without a manual
 *     ChangeDetectorRef poke. Call it from AppComponent.ngOnInit rather than from
 *     a constructor that may run outside the zone.
 */

import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class ConnectivityService {
  // #region Fields
  /**
   * Current connectivity, seeded from the browser flag so a subscriber that
   * arrives after a drop (a lazily created component, say) gets the real state
   * rather than an optimistic default.
   */
  private readonly _isOnline = new BehaviorSubject<boolean>(navigator.onLine);

  /** Guards against wiring the window listeners more than once. */
  private _initialized = false;
  // #endregion

  // #region Lifecycle / Init
  /**
   * Starts watching for connectivity changes. Call once at app startup (from
   * AppComponent); calling it again is a no-op.
   *
   * Both events are needed rather than polling `navigator.onLine`: the browser
   * pushes the transition, and a poll would either lag behind it or burn a timer
   * on a battery-powered device for the same answer.
   */
  public init(): void {
    if (this._initialized) {
      return;
    }
    this._initialized = true;

    window.addEventListener('online', () => this.publish(true));
    window.addEventListener('offline', () => this.publish(false));
  }
  // #endregion

  // #region State
  /**
   * Emits the current connectivity and every subsequent change.
   *
   * Deduplicated, so a browser that fires 'online' more than once for a single
   * reconnection (moving between access points does this) cannot make a banner
   * flicker back into view.
   */
  public get isOnline$(): Observable<boolean> {
    return this._isOnline.pipe(distinctUntilChanged());
  }

  /** The current connectivity, for a one-off check that isn't worth a subscription. */
  public get isOnline(): boolean {
    return this._isOnline.value;
  }

  /**
   * Re-reads the browser's connectivity flag and republishes it. This is what the
   * banner's "Retry" is wired to.
   *
   * It intentionally does not probe the network. There is no held request to
   * retry — the banner blocks nothing — and a fetch would only report what the
   * 'online' event already delivers by itself. What the button does buy is
   * recovery from a stale flag, which some webviews and iOS lock-screen wakeups
   * leave behind by missing the event, plus the plain confirmation of "still
   * nothing", which is the answer someone standing in a dead spot is asking for.
   *
   * @returns True if the device now reports a connection.
   */
  public recheck(): boolean {
    const isOnline = navigator.onLine;
    this.publish(isOnline);
    return isOnline;
  }
  // #endregion

  // #region Internals
  /**
   * Pushes a new connectivity value to subscribers.
   *
   * @param isOnline - Whether the device reports a connection.
   */
  private publish(isOnline: boolean): void {
    this._isOnline.next(isOnline);
  }
  // #endregion
}
